import { AddInError } from '@utils-global';
import { AxiosError } from 'axios';

export type GraphMimeFailureCause =
  | 'itemNotFound' // item moved/deleted or not yet synced in cached mode
  | 'invalidId' // outlook returned an id Graph rejects as malformed - often retry helps
  | 'accessDenied' // caller lacks rights on the item/mailbox (IT can grant access)
  | 'sessionExpired' // token missing/expired/rejected (user can re-authenticate)
  | 'mailboxUnavailable' // mailbox missing or not reachable over Graph/REST (IT can help)
  | 'serverBusy' // throttling / temporarily unavailable - transient
  | 'mimeGeneration' // server couldn't produce valid MIME for the item
  | 'offline' // client has no network
  | 'graphError' // a named Graph/Exchange code we don't special-case
  | 'unknown'; // no recognizable error code - likely unexpected

export type GraphMimeDiagnosis = {
  cause: GraphMimeFailureCause;
  errorCode?: string;
  errorMessage?: string;
  statusCode?: number;
};

export class GraphMimeError extends AddInError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, context);
    this.name = 'GraphMimeError';
    Object.setPrototypeOf(this, GraphMimeError.prototype);
  }
}

export const mapGraphErrorCode = (errorCode: string | undefined): GraphMimeFailureCause => {
  switch (errorCode) {
    // Item moved/deleted, or not yet synced to the server in cached mode.
    case 'ErrorItemNotFound':
      return 'itemNotFound';
    // Outlook sporadically hands an item id that Graph can't resolve.
    case 'ErrorInvalidIdMalformed':
      return 'invalidId';
    // Caller lacks rights on the item/mailbox - IT can grant access.
    case 'ErrorAccessDenied':
    case 'accessDenied':
      return 'accessDenied';
    // Token missing/expired/rejected - the user can fix this by signing in again.
    case 'InvalidAuthenticationToken':
    case 'unauthorized':
      return 'sessionExpired';
    // Mailbox doesn't exist or isn't reachable over Graph/REST (typically an on-prem/hybrid
    // or unprovisioned mailbox) - IT needs to sort this out.
    case 'ErrorNonExistentMailbox':
    case 'MailboxNotEnabledForRESTAPI':
      return 'mailboxUnavailable';
    // Throttling / temporarily unavailable / transient (Exchange or Graph generic). These
    // are retried in getMailMIME; if they persist they surface here as an expected "busy,
    // try again" condition rather than a bug.
    case 'ErrorServerBusy':
    case 'ApplicationThrottled':
    case 'CommandConcurrencyLimitReached':
    case 'tooManyRequests':
    case 'serviceUnavailable':
    case 'ServiceUnavailable':
    case 'gatewayTimeout':
    case 'ErrorMailboxMoveInProgress':
    case 'ErrorTimeoutExpired':
      return 'serverBusy';
    // Server couldn't generate/return valid MIME for the item.
    case 'ErrorMimeContentConversionFailed':
    case 'ErrorMimeContentInvalid':
    case 'ErrorMimeContentInvalidBase64String':
      return 'mimeGeneration';
    // Graph's generic catch-all code carries no signal (typically with an empty message);
    // treat it as "no code" so classification falls back to the HTTP status.
    case 'UnknownError':
      return 'unknown';
    default:
      // A named code we don't special-case is kept verbatim; no code at all is unexpected.
      return errorCode ? 'graphError' : 'unknown';
  }
};

const mapGraphHttpStatus = (statusCode: number | undefined): GraphMimeFailureCause | undefined => {
  switch (statusCode) {
    case 401:
      return 'sessionExpired';
    case 403:
      return 'accessDenied';
    case 404:
      return 'itemNotFound';
    // Request timeout / throttled / temporarily unavailable / gateway timeout — all transient.
    case 408:
    case 429:
    case 500:
    case 503:
    case 504:
      return 'serverBusy';
    default:
      return undefined;
  }
};

export const classifyGraphMimeFailure = (error: unknown): GraphMimeDiagnosis => {
  const axiosError = error as AxiosError<{ error?: { code?: string; message?: string } }>;

  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const isAxiosErrNetwork = axiosError?.code === 'ERR_NETWORK';
  if (isOffline || isAxiosErrNetwork) {
    return { cause: 'offline' };
  }

  const errorData = axiosError?.response?.data;
  const errorCode = errorData?.error?.code;
  const errorMessage = errorData?.error?.message;
  const statusCode = axiosError?.response?.status;

  // The body's `error.code` is the primary signal; only when it's absent entirely (cause
  // `unknown`) do we fall back to the HTTP status to identify transient/expected failures.
  const codeCause = mapGraphErrorCode(errorCode);
  const cause = codeCause === 'unknown' ? (mapGraphHttpStatus(statusCode) ?? codeCause) : codeCause;

  return {
    cause,
    errorCode,
    errorMessage,
    statusCode,
  };
};

export const isExpectedGraphMimeFailure = (cause: GraphMimeFailureCause): boolean =>
  cause !== 'graphError' && cause !== 'unknown';
